import { ExamComment } from 'common/ExamComment/ExamComment';
import { Stack } from 'common/ui/Layout';
import Text from 'common/ui/Text';
import { ExamComment as ExamCommentType } from 'generated/graphql';
import { ExamCommentComposer } from 'common/ExamComment/ExamCommentComposer';
import { useCallback, useMemo, useRef, useLayoutEffect, useState } from 'react';
import { useValidateUserHasRbacPermissions } from 'hooks/useValidateUserHasRbacPermissions';
import { PermissionName } from '@sironamedical/common';
import { useExamComments } from 'common/ExamComment/useExamComments';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';
import { PATIENT_JACKET_CONTAINER_SPACING } from 'domains/PatientJacket/constants';
import { NOOP } from 'config/constants';
import { telemetryContextFor } from 'modules/Apollo/types';

const MAX_COMPOSER_HEIGHT = 100;

export const ExamCommentsTab = ({
  worklistItemSmid,
  comments = [],
  loading,
  heightPx,
  onAddComment = NOOP,
  onChangeComment = NOOP,
  showSendButton = true,
}: {
  worklistItemSmid: string;
  comments: ReadonlyArray<ExamCommentType>;
  loading: boolean;
  heightPx: number;
  onAddComment?: () => void;
  onChangeComment?: (comment: string) => void;
  showSendButton?: boolean;
}): React.ReactElement => {
  const hasWriteAccess = useValidateUserHasRbacPermissions({
    permissionNames: [PermissionName.ExamCommentReadWriteAccess],
  });

  const [draftComment, setDraftComment] = useState('');

  const setComment = useCallback(
    (comment: string) => {
      setDraftComment(comment);
      onChangeComment(comment);
    },
    [onChangeComment]
  );

  const { addExamComment } = useExamComments({ context: APOLLO_QUERY_CONTEXT });

  const listRef = useRef<HTMLDivElement | null>(null);
  const shouldScrollRef = useRef(false);

  const onSend = useCallback(async () => {
    shouldScrollRef.current = true;
    await addExamComment({
      worklistItemSmid,
      comment: draftComment,
      onComplete: () => {
        setComment('');
        onAddComment?.();
      },
    });
  }, [addExamComment, worklistItemSmid, draftComment, setComment, onAddComment]);

  const hasNoComments = useMemo(() => !loading && comments.length === 0, [loading, comments]);

  useLayoutEffect(() => {
    if (!shouldScrollRef.current) {
      return;
    }

    const scroller = listRef.current;
    if (!scroller) {
      return;
    }

    const lastElement = scroller.lastElementChild;

    if (lastElement) {
      scrollIntoViewIfNeeded(lastElement, {
        behavior: 'smooth',
        block: 'end',
        scrollMode: 'if-needed',
      });
    }
    shouldScrollRef.current = false;
  }, [comments.length]);

  const items = useMemo(
    () =>
      comments.filter(Boolean).map((c, i) => (
        <div key={`${c?.createdAt}-${i}`}>
          <ExamComment examComment={c} />
        </div>
      )),
    [comments]
  );

  return (
    <Stack
      vertical
      css={`
        height: ${heightPx - PATIENT_JACKET_CONTAINER_SPACING}px;
        overflow: hidden;
      `}
    >
      <Stack
        vertical
        ref={listRef}
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {hasNoComments ? (
          <Stack alignY="center" alignX="center" css="height: 100%">
            <Text>No exam comments available</Text>
          </Stack>
        ) : (
          items
        )}
      </Stack>

      {hasWriteAccess && (
        <div style={{ flex: '0 0 auto' }}>
          <Stack alignY="end">
            <ExamCommentComposer
              comment={draftComment}
              setComment={setComment}
              onSend={onSend}
              maxHeight={MAX_COMPOSER_HEIGHT}
              showSendButton={showSendButton}
            />
          </Stack>
        </div>
      )}
    </Stack>
  );
};

ExamCommentsTab.displayName = 'ExamCommentsTab';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(ExamCommentsTab);
